import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const PAGE = 100;

type Site = 'ancestry' | 'myheritage' | 'familyfinder';
const SITE_NAMES: Record<Site, string> = { ancestry: 'Ancestry', myheritage: 'MyHeritage', familyfinder: 'Family Finder' };

interface SiteEntry {
  name: string;
  shared_cm: number | null;
  segments?: number | null;
  longest_cm?: number | null;
  relationship?: string | null;
  url?: string | null;
  x_cm?: number | null;
}

interface Person {
  key: string;
  name: string;
  sites: Partial<Record<Site, SiteEntry[]>>;
  labels: string[];
  lines: string[];
  side: string | null;
  suggested_lines: string[];
  suggested_side: string | null;
  cluster: string | null;
  notes: string[];
  shared_surnames: string[];
  max_cm: number | null;
  site_count: number;
  in_painter: boolean;
  in_tree: boolean;
}

interface Counts {
  people: number;
  by_site: Partial<Record<Site, number>>;
  multi_site: number;
  by_side: Record<string, number>;
  labels: Record<string, number>;
  suggestions: number;
}

interface Cluster {
  cluster: string;
  size: number;
  members: { name: string; max_cm: number | null; lines: string[]; side: string | null }[];
  lines: Record<string, number>;
  sides: Record<string, number>;
  mixed_sides: boolean;
  unlabelled: number;
}

const UPLOADS: { source: string; label: string; hint: string }[] = [
  { source: 'ancestry', label: 'Ancestry match list', hint: 'DNA Matches CSV' },
  { source: 'myheritage', label: 'MyHeritage match list', hint: 'Match List CSV' },
  { source: 'autoclusters', label: 'MyHeritage AutoClusters', hint: 'the CSV inside the AutoClusters zip' },
];

const sideLabel = (s?: string | null) =>
  s === 'Paternal' ? "Father's side" : s === 'Maternal' ? "Mother's side" : s === 'Both' ? 'Both sides' : s === 'Conflict' ? 'Conflicting' : '';

const DnaMatchesPage: React.FC = () => {
  const [people, setPeople] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [showClusters, setShowClusters] = useState(false);
  const [loading, setLoading] = useState(true);

  const [side, setSide] = useState('');
  const [label, setLabel] = useState('');
  const [minCm, setMinCm] = useState('20');
  const [multiSite, setMultiSite] = useState(false);
  const [suggestedOnly, setSuggestedOnly] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async (offset = 0) => {
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset), min_cm: minCm || '0' });
    if (side) params.set('side', side);
    if (label) params.set('label', label);
    if (multiSite) params.set('multi_site', 'true');
    if (suggestedOnly) params.set('suggested_only', 'true');
    if (search.trim()) params.set('q', search.trim());
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/matches/combined?${params}`);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      setTotal(data.total);
      setCounts(data.counts);
      setPeople(prev => (offset === 0 ? data.people : [...prev, ...data.people]));
      setError(null);
    } catch (e) {
      setError(`Could not load matches (${e instanceof Error ? e.message : 'unknown error'})`);
    } finally {
      setLoading(false);
    }
  }, [side, label, minCm, multiSite, suggestedOnly, search]);

  const loadClusters = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/matches/clusters`);
      if (res.ok) setClusters((await res.json()).clusters);
    } catch {
      /* the clusters panel is optional */
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(0), 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { loadClusters(); }, [loadClusters]);

  const upload = async (source: string, file: File | undefined) => {
    if (!file) return;
    setUploading(source);
    setUploadMessage(null);
    try {
      const res = await fetch(`${API_URL}/matches/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, csv_text: await file.text() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `API returned ${res.status}`);
      setUploadMessage(source === 'autoclusters'
        ? `Imported ${data.imported} clustered matches in ${data.clusters} clusters.`
        : `Imported ${data.imported} matches (${data.labelled} with your group labels).`);
      await load(0);
      await loadClusters();
    } catch (e) {
      setUploadMessage(`Import failed: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setUploading(null);
    }
  };

  return (
    <section className="genealogy-section family-finder-page">
      <h1 className="heading-1">DNA Matches</h1>
      <p className="family-finder-intro">
        One list of your matches across Ancestry, MyHeritage and <Link to="/family-finder">Family Finder</Link>, linked
        by name, with your group labels from each site. People in the same MyHeritage AutoCluster usually share an
        ancestral line, so unlabelled cluster members get a suggested line from their labelled cluster-mates.
        Importing a site again replaces its previous list.
      </p>

      <div className="genealogy-card family-finder-upload">
        {UPLOADS.map(u => (
          <label key={u.source} className="genealogy-btn-primary family-finder-file-btn" title={`Choose ${u.hint}`}>
            {uploading === u.source ? 'Importing…' : u.label}
            <input type="file" accept=".csv,text/csv" disabled={!!uploading} onChange={e => { upload(u.source, e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        ))}
        {uploadMessage && <div className="family-finder-import-message">{uploadMessage}</div>}
      </div>

      {error && <div className="family-finder-error">{error}</div>}
      {loading && !counts && !error && (
        <div className="family-finder-loading">Loading matches… the first load after an import can take a few seconds.</div>
      )}

      {counts && counts.people > 0 && (
        <>
          <div className="family-finder-stats">
            <div className="genealogy-card"><strong>{counts.people.toLocaleString()}</strong><span>people</span></div>
            {(Object.keys(SITE_NAMES) as Site[]).map(s => (
              <div className="genealogy-card" key={s}><strong>{(counts.by_site[s] || 0).toLocaleString()}</strong><span>{SITE_NAMES[s]}</span></div>
            ))}
            <div className="genealogy-card"><strong>{counts.multi_site}</strong><span>on 2+ sites</span></div>
            <div className="genealogy-card"><strong>{counts.suggestions}</strong><span>cluster suggestions</span></div>
            <div className="genealogy-card"><strong>{counts.by_side.Conflict || 0}</strong><span>conflicting sides</span></div>
          </div>

          <div className="genealogy-card family-finder-panel">
            <div className="family-finder-filters">
              <select value={side} onChange={e => setSide(e.target.value)} aria-label="Side">
                <option value="">Any side</option>
                <option value="Paternal">Father's side</option>
                <option value="Maternal">Mother's side</option>
                <option value="Both">Both sides</option>
                <option value="Conflict">Conflicting</option>
                <option value="Unknown">Unknown</option>
              </select>
              <select value={label} onChange={e => setLabel(e.target.value)} aria-label="Group label">
                <option value="">Any group label</option>
                {Object.entries(counts.labels).map(([l, n]) => <option key={l} value={l}>{l} ({n})</option>)}
              </select>
              <label>Min cM <input type="number" min={0} value={minCm} onChange={e => setMinCm(e.target.value)} /></label>
              <label><input type="checkbox" checked={multiSite} onChange={e => setMultiSite(e.target.checked)} /> On 2+ sites</label>
              <label><input type="checkbox" checked={suggestedOnly} onChange={e => setSuggestedOnly(e.target.checked)} /> Cluster suggestions</label>
              <input type="search" placeholder="Name, label or surname" value={search} onChange={e => setSearch(e.target.value)} />
              <span className="family-finder-hint">{loading ? 'Updating…' : `${total.toLocaleString()} people`}</span>
            </div>
            <div className="family-finder-table-wrap">
              <table className="family-finder-table">
                <thead>
                  <tr><th>Match</th><th>Best cM</th><th>Sites</th><th>Side</th><th>Lines / groups</th><th>Cluster</th></tr>
                </thead>
                <tbody>
                  {people.map(p => (
                    <tr key={p.key} className={p.side === 'Conflict' ? 'ff-row-conflict' : ''}>
                      <td>
                        {p.name}
                        {p.in_tree && <span className="ff-badge ff-badge-tree">in tree</span>}
                        {p.in_painter && <span className="ff-badge">DNA Painter</span>}
                        {p.notes.length > 0 && <div className="family-finder-hint">{p.notes.join(' · ')}</div>}
                      </td>
                      <td>{p.max_cm ?? ''}</td>
                      <td className="dna-matches-sites">
                        {(Object.keys(p.sites) as Site[]).map(s => p.sites[s]!.map((e, i) => (
                          <span key={`${s}${i}`} className="ff-badge" title={e.relationship || ''}>
                            {e.url ? <a href={e.url} target="_blank" rel="noopener noreferrer">{SITE_NAMES[s]}</a> : SITE_NAMES[s]} {e.shared_cm}
                            {e.x_cm ? ` (X ${e.x_cm})` : ''}
                          </span>
                        )))}
                      </td>
                      <td>
                        {sideLabel(p.side)}
                        {p.suggested_side && <div className="dna-matches-suggestion">suggested: {sideLabel(p.suggested_side)}</div>}
                      </td>
                      <td>
                        {p.lines.join(', ')}
                        {p.suggested_lines.length > 0 && <div className="dna-matches-suggestion">suggested: {p.suggested_lines.join(', ')}</div>}
                        {p.shared_surnames.length > 0 && <div className="family-finder-hint">shared surnames: {p.shared_surnames.join(', ')}</div>}
                      </td>
                      <td>{p.cluster ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {people.length < total && (
              <button type="button" className="genealogy-btn-secondary" onClick={() => load(people.length)}>Show more</button>
            )}
          </div>

          {clusters.length > 0 && (
            <div className="genealogy-card family-finder-panel">
              <h2 className="heading-3">
                AutoClusters ({clusters.length}){' '}
                <button type="button" className="genealogy-btn-secondary" onClick={() => setShowClusters(v => !v)}>
                  {showClusters ? 'Hide' : 'Show'}
                </button>
              </h2>
              <p className="family-finder-hint">
                {clusters.filter(c => c.mixed_sides).length} cluster(s) have members labelled on both sides; check those labels.
              </p>
              {showClusters && (
                <div className="dna-matches-clusters">
                  {clusters.map(c => (
                    <div key={c.cluster} className={`dna-matches-cluster${c.mixed_sides ? ' is-mixed' : ''}`}>
                      <div>
                        <strong>Cluster {c.cluster}</strong> · {c.size} people
                        {Object.keys(c.lines).length > 0 && <> · {Object.entries(c.lines).map(([l, n]) => `${l} (${n})`).join(', ')}</>}
                        {c.mixed_sides && <span className="ff-conflict"> · mixed sides: {Object.entries(c.sides).map(([s, n]) => `${sideLabel(s)} ${n}`).join(', ')}</span>}
                      </div>
                      <div className="family-finder-hint">
                        {c.members.map(m => `${m.name}${m.side ? ` [${sideLabel(m.side)}]` : ''}`).join(' · ')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {counts && counts.people === 0 && !error && <p>No match lists imported yet.</p>}
    </section>
  );
};

export default DnaMatchesPage;
